// The UGC clip program (/creators): one payload query, the per-creator page,
// the month payout run, and the mutations. Every write invalidates the
// program payload; nothing here can send outreach or move money — that's a
// property of the API, not this file.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type {
  ApproveWaitlistRequest, BatchReviewRequest, CreatorBonus, CreatorDetail, CreatorProgramPayload, DecideApplicationRequest,
  MarkMonthPaidRequest, OpenBonusDropRequest, PayoutRunRow, PayoutSkip, UpdateProgramCreatorRequest,
} from '@shared/types/creatorProgram';
import type { DropAllocation } from '@shared/types/creatorClips';
import type { InboxPayload, InboxThreadDetail } from '@shared/types/creatorMessages';
import type { ImportProspectsRequest, ProspectInput, UpdateProspectRequest } from '@shared/types/creatorClips';

export const PROGRAM_KEY = ['admin', 'creators', 'program'];
const BADGE_KEY = ['admin', 'creators', 'badge'];

export function useCreatorProgram() {
  return useQuery({
    queryKey: PROGRAM_KEY,
    queryFn: async () => {
      const res = await api.get<CreatorProgramPayload>('/admin/creators/program');
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load');
      return res.data;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useCreatorDetail(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'creators', 'program', 'creator', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await api.get<CreatorDetail>(`/admin/creators/program/creators/${id}`);
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load');
      return res.data;
    },
    staleTime: 30_000,
  });
}

export interface PayoutRun { rows: PayoutRunRow[]; csv: string; skipped: PayoutSkip[] }

export function usePayoutRun(month: string | null) {
  return useQuery({
    queryKey: ['admin', 'creators', 'program', 'payouts', month],
    enabled: !!month,
    queryFn: async () => {
      const res = await api.get<PayoutRun>(`/admin/creators/program/payouts?month=${month}`);
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load');
      return res.data;
    },
    staleTime: 15_000,
  });
}

/** The nav badge: pending-review count, polled. Only mounted for roles that can open /creators. */
export function useCreatorPendingCount(enabled: boolean) {
  const q = useQuery({
    queryKey: BADGE_KEY,
    enabled,
    queryFn: async () => {
      const res = await api.get<{ pendingCount: number; unreadMessages?: number }>('/admin/creators/program/badge');
      return (res.data?.pendingCount ?? 0) + (res.data?.unreadMessages ?? 0);
    },
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: true,
  });
  return q.data ?? 0;
}

export function useCreatorProgramMutations() {
  const qc = useQueryClient();
  const done = () => {
    void qc.invalidateQueries({ queryKey: PROGRAM_KEY });
    void qc.invalidateQueries({ queryKey: BADGE_KEY });
    // The partnerships tab shares the roster.
    void qc.invalidateQueries({ queryKey: ['admin', 'creators'] });
  };

  const review = useMutation({
    mutationFn: (body: BatchReviewRequest) =>
      api.post<{ reviewed: number; skipped: number; overLimit: number }>('/admin/creators/clips/review-batch', body),
    onSuccess: done,
  });

  const setUsage = useMutation({
    mutationFn: ({ id, usedInAd }: { id: string; usedInAd: string | null }) =>
      api.patch(`/admin/creators/clips/submissions/${id}/usage`, { usedInAd }),
    onSuccess: done,
  });

  const updateCreator = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateProgramCreatorRequest) =>
      api.patch(`/admin/creators/program/creators/${id}`, body),
    onSuccess: done,
  });

  const markMonthPaid = useMutation({
    mutationFn: (body: MarkMonthPaidRequest) =>
      api.post<{ id: string; amountCents: number; clipCount: number }>('/admin/creators/program/payouts/mark-paid', body),
    onSuccess: done,
  });

  const addProspect = useMutation({
    mutationFn: (body: ProspectInput) =>
      api.post<{ inserted: number; skipped: number }>('/admin/creators/prospects', body),
    onSuccess: done,
  });

  const importProspects = useMutation({
    mutationFn: (body: ImportProspectsRequest) =>
      api.post<{ inserted: number; skipped: number }>('/admin/creators/prospects/import', body),
    onSuccess: done,
  });

  const updateProspect = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateProspectRequest) =>
      api.patch(`/admin/creators/prospects/${id}`, body),
    onSuccess: done,
  });

  const onboardProspect = useMutation({
    mutationFn: (id: string) => api.post<{ creatorId: string }>(`/admin/creators/prospects/${id}/onboard`),
    onSuccess: done,
  });

  const runNotifier = useMutation({
    mutationFn: () => api.post<{ posted: boolean; text: string | null; newClips: number; error: string | null }>('/admin/creators/cron'),
    onSuccess: done,
  });

  // Applications: approve / waitlist / decline. The server emails them (soft).
  const decideApplication = useMutation({
    mutationFn: ({ portalUserId, ...body }: { portalUserId: string } & DecideApplicationRequest) =>
      api.post<{ status: string }>(`/admin/creators/program/applications/${portalUserId}/decide`, body),
    onSuccess: done,
  });

  // Drops: the global default allocation, the month's "what we need most", bonus slots.
  const approveWaitlist = useMutation({
    mutationFn: (body: ApproveWaitlistRequest) =>
      api.post<{ approved: number; skipped: number }>('/admin/creators/program/applications/waitlist/approve', body),
    onSuccess: done,
  });

  const setDefaultAllocation = useMutation({
    mutationFn: (allocation: DropAllocation) => api.patch('/admin/creators/program/drops/default', { allocation }),
    onSuccess: done,
  });
  const setDropNote = useMutation({
    mutationFn: ({ key, needsNote }: { key: string; needsNote: string | null }) =>
      api.patch(`/admin/creators/program/drops/${key}`, { needsNote }),
    onSuccess: done,
  });
  const openBonus = useMutation({
    mutationFn: ({ creatorId, ...body }: { creatorId: string } & OpenBonusDropRequest) =>
      api.post<CreatorBonus>(`/admin/creators/program/creators/${creatorId}/bonus`, body),
    onSuccess: done,
  });

  return {
    review, setUsage, updateCreator, markMonthPaid, addProspect, importProspects, updateProspect, onboardProspect, runNotifier,
    decideApplication, approveWaitlist, setDefaultAllocation, setDropNote, openBonus,
  };
}

// ── Inbox (creator questions) ───────────────────────────────────────────────

export const INBOX_KEY = ['admin', 'creators', 'inbox'];

export function useCreatorInbox(enabled = true) {
  return useQuery({
    queryKey: INBOX_KEY,
    enabled,
    queryFn: async () => {
      const res = await api.get<InboxPayload>('/admin/creators/program/inbox');
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load');
      return res.data;
    },
    staleTime: 5_000,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useCreatorThread(portalUserId: string | null) {
  return useQuery({
    queryKey: [...INBOX_KEY, portalUserId],
    enabled: !!portalUserId,
    queryFn: async () => {
      const res = await api.get<InboxThreadDetail>(`/admin/creators/program/inbox/${portalUserId}`);
      if (!res.success || !res.data) throw new Error(res.error || 'Failed to load');
      return res.data;
    },
    staleTime: 2_000,
    // Live chat: poll while the tab is open.
    refetchInterval: 3_000,
    refetchIntervalInBackground: false,
  });
}

export function useReplyToCreator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ portalUserId, body }: { portalUserId: string; body: string }) =>
      api.post<{ emailed: 'sent' | 'failed' | 'online' | 'cooldown' }>(`/admin/creators/program/inbox/${portalUserId}/reply`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INBOX_KEY });
      void qc.invalidateQueries({ queryKey: BADGE_KEY });
    },
  });
}
